import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Spinner,
  UserPlus,
  SignIn,
  BookOpen,
  Certificate,
  Globe,
  UserCircleMinus,
  Trash,
  Pencil,
  ClockCounterClockwise,
  FunnelSimple,
  CaretDown
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_CONFIG = {
  user_registered: { label: 'Novo Registro', icon: UserPlus, color: '#27AE60', bg: 'bg-[#27AE60]/10' },
  user_login: { label: 'Login', icon: SignIn, color: '#2980B9', bg: 'bg-[#2980B9]/10' },
  enrollment: { label: 'Matricula', icon: BookOpen, color: '#8E44AD', bg: 'bg-[#8E44AD]/10' },
  certificate_generated: { label: 'Certificado', icon: Certificate, color: '#F39C12', bg: 'bg-[#F39C12]/10' },
  course_created: { label: 'Curso Criado', icon: BookOpen, color: '#27AE60', bg: 'bg-[#27AE60]/10' },
  course_deleted: { label: 'Curso Excluido', icon: Trash, color: '#E74C3C', bg: 'bg-[#E74C3C]/10' },
  course_assigned: { label: 'Curso Atribuido', icon: BookOpen, color: '#2C3E50', bg: 'bg-[#2C3E50]/10' },
  user_status_changed: { label: 'Status Alterado', icon: UserCircleMinus, color: '#E74C3C', bg: 'bg-[#E74C3C]/10' },
  domain_added: { label: 'Dominio Adicionado', icon: Globe, color: '#2980B9', bg: 'bg-[#2980B9]/10' },
  domain_removed: { label: 'Dominio Removido', icon: Globe, color: '#E74C3C', bg: 'bg-[#E74C3C]/10' },
};

const FILTER_OPTIONS = [
  { value: '', label: 'Todas as Atividades' },
  { value: 'user_registered', label: 'Registros' },
  { value: 'user_login', label: 'Logins' },
  { value: 'enrollment', label: 'Matriculas' },
  { value: 'certificate_generated', label: 'Certificados' },
  { value: 'course_created', label: 'Cursos Criados' },
  { value: 'course_deleted', label: 'Cursos Excluidos' },
  { value: 'domain_added', label: 'Dominios Adicionados' },
  { value: 'domain_removed', label: 'Dominios Removidos' },
  { value: 'user_status_changed', label: 'Status Alterados' },
];

function formatTimeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Agora mesmo';
  if (diffMin < 60) return `${diffMin} min atras`;
  if (diffHrs < 24) return `${diffHrs}h atras`;
  if (diffDays < 7) return `${diffDays}d atras`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function AdminActivityLog() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filter) params.append('action', filter);
      const response = await axios.get(`${API_URL}/api/activity-logs?${params}`, { withCredentials: true });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/activity-logs/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setPage(0);
  }, [filter]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const currentFilter = FILTER_OPTIONS.find(f => f.value === filter) || FILTER_OPTIONS[0];

  if (loading && page === 0) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-activity-log">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Log de Atividades</h1>
            <p className="text-[#5C6B7A]">Acompanhe todas as acoes realizadas na plataforma</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 border-[#BDC3C7]" data-testid="filter-btn">
                <FunnelSimple size={18} />
                {currentFilter.label}
                <CaretDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {FILTER_OPTIONS.map(opt => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => setFilter(opt.value)}
                  className={`cursor-pointer ${filter === opt.value ? 'bg-[#F0F2F5] font-medium' : ''}`}
                  data-testid={`filter-${opt.value || 'all'}`}
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-[#2C3E50]/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#27AE60]" data-testid="stat-registrations">{stats.registrations}</p>
                <p className="text-xs text-[#5C6B7A] mt-1">Registros</p>
              </CardContent>
            </Card>
            <Card className="border-[#2C3E50]/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#2980B9]" data-testid="stat-logins">{stats.logins_today}</p>
                <p className="text-xs text-[#5C6B7A] mt-1">Logins Hoje</p>
              </CardContent>
            </Card>
            <Card className="border-[#2C3E50]/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#8E44AD]" data-testid="stat-enrollments">{stats.enrollments}</p>
                <p className="text-xs text-[#5C6B7A] mt-1">Matriculas</p>
              </CardContent>
            </Card>
            <Card className="border-[#2C3E50]/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#F39C12]" data-testid="stat-certificates">{stats.certificates}</p>
                <p className="text-xs text-[#5C6B7A] mt-1">Certificados</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Timeline */}
        <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
          {logs.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-logs">
              <ClockCounterClockwise size={48} className="mx-auto text-[#BDC3C7] mb-4" />
              <p className="text-[#5C6B7A]">Nenhuma atividade registrada ainda</p>
            </div>
          ) : (
            <div className="divide-y divide-[#F0F2F5]">
              {logs.map((log, i) => {
                const config = ACTION_CONFIG[log.action] || { label: log.action, icon: ClockCounterClockwise, color: '#5C6B7A', bg: 'bg-gray-100' };
                const Icon = config.icon;
                return (
                  <div key={i} className="flex items-start gap-4 px-6 py-4 hover:bg-[#F8F9FA]/50 transition-colors" data-testid={`log-entry-${i}`}>
                    <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon size={20} style={{ color: config.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#1A252F]">{log.user_name}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: config.color + '15', color: config.color }}>
                          {config.label}
                        </span>
                      </div>
                      {log.details && (
                        <p className="text-sm text-[#5C6B7A] mt-0.5 truncate">{log.details}</p>
                      )}
                    </div>
                    <span className="text-xs text-[#95A5A6] whitespace-nowrap flex-shrink-0">
                      {formatTimeAgo(log.created_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#F0F2F5] bg-[#F8F9FA]">
              <p className="text-sm text-[#5C6B7A]">
                Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="prev-page"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="next-page"
                >
                  Proximo
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminActivityLog;
